import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type InviteRow = Database['public']['Tables']['employer_candidate_invites']['Row'];
type CatalogJob = Pick<
  Database['public']['Tables']['seek_job_catalog']['Row'],
  | 'id'
  | 'hire_org_id'
  | 'title'
  | 'org_name'
  | 'location_text'
  | 'city'
  | 'country'
  | 'employment_type'
  | 'work_mode'
  | 'salary_min'
  | 'salary_max'
  | 'salary_currency'
  | 'salary_visibility_mode'
  | 'seniority'
  | 'description'
  | 'department'
  | 'min_experience_years'
  | 'skills_json'
  | 'requirements_json'
  | 'certifications_json'
  | 'languages_json'
  | 'closes_at'
  | 'published_at'
>;

export interface CandidateInvite extends InviteRow {
  // Joined from seek_job_catalog (optional — may not exist for all jobs)
  catalog_job?: CatalogJob | null;
}

export function useSeekInvites() {
  const { user } = useSeekAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const uid = user?.id;

  const query = useQuery({
    queryKey: ['seek-invites', uid],
    queryFn: async () => {
      const { data: invitesData, error } = await seekSupabase
        .from('employer_candidate_invites')
        .select('*')
        .eq('candidate_id', uid!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const invites = invitesData ?? [];

      // ── Mark unaccessed invites as accessed (first view) ──
      const unaccessed = invites.filter(
        (i) => !i.accessed_at && (i.invite_status === 'pending' || i.invite_status === 're-invited')
      );
      if (unaccessed.length > 0) {
        const nowIso = new Date().toISOString();
        for (const inv of unaccessed) {
          seekSupabase
            .from('employer_candidate_invites')
            .update({ accessed_at: nowIso })
            .eq('id', inv.id)
            .then(() => {});
          inv.accessed_at = nowIso;
        }
      }

      // Try to enrich with seek_job_catalog data for extra details
      const jobIds = [...new Set(invites
        .map((invite) => invite.target_hire_job_id)
        .filter((jobId): jobId is string => typeof jobId === 'string'))];
      const catalogMap: Record<string, CatalogJob> = {};
      if (jobIds.length > 0) {
        const { data: jobs } = await seekSupabase
          .from('seek_job_catalog')
          .select('id, hire_job_id, hire_org_id, title, org_name, location_text, city, country, employment_type, work_mode, salary_min, salary_max, salary_currency, salary_visibility_mode, seniority, description, department, min_experience_years, skills_json, requirements_json, certifications_json, languages_json, closes_at, published_at')
          .in('hire_job_id', jobIds);
        if (jobs) {
          for (const j of jobs) {
            catalogMap[j.hire_job_id] = {
              id: j.id,
              hire_org_id: j.hire_org_id,
              title: j.title,
              org_name: j.org_name,
              location_text: j.location_text,
              city: j.city,
              country: j.country,
              employment_type: j.employment_type,
              work_mode: j.work_mode,
              salary_min: j.salary_min,
              salary_max: j.salary_max,
              salary_currency: j.salary_currency,
              salary_visibility_mode: j.salary_visibility_mode,
              seniority: j.seniority,
              description: j.description,
              department: j.department,
              min_experience_years: j.min_experience_years,
              skills_json: j.skills_json,
              requirements_json: j.requirements_json,
              certifications_json: j.certifications_json,
              languages_json: j.languages_json,
              closes_at: j.closes_at,
              published_at: j.published_at,
            };
          }
        }
      }

      return invites.map<CandidateInvite>((inv) => ({
        ...inv,
        job_title: inv.job_title ?? null,
        org_name: inv.org_name ?? null,
        catalog_job: inv.target_hire_job_id ? (catalogMap[inv.target_hire_job_id] ?? null) : null,
      }));
    },
    enabled: !!uid,
  });

  const respond = useMutation({
    mutationFn: async ({ inviteId, status }: { inviteId: string; status: 'accepted' | 'declined' }) => {
      if (status === 'accepted') {
        const { data, error } = await seekSupabase.functions.invoke('accept-invite', {
          body: { invite_id: inviteId },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      } else {
        // Use edge function for decline to trigger employer notifications
        const { data, error } = await seekSupabase.functions.invoke('decline-invite', {
          body: { invite_id: inviteId },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['seek-invites', uid] });
      qc.invalidateQueries({ queryKey: ['seek-applications'] });
      qc.invalidateQueries({ queryKey: ['seek-jobs'] });
      toast({ title: vars.status === 'accepted' ? 'Invite accepted! Application submitted.' : 'Invite declined' });
    },
    onError: (e) => {
      toast({ title: 'Failed to respond', description: e instanceof Error ? e.message : 'Please try again.', variant: 'destructive' });
    },
  });

  return {
    invites: query.data ?? [],
    isLoading: query.isLoading,
    pendingCount: (query.data ?? []).filter(i => i.invite_status === 'pending').length,
    respond: respond.mutate,
    isResponding: respond.isPending,
  };
}
